import { useState, useEffect, useCallback, useRef } from "preact/hooks"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Conversation, createConversation, CreateConversationRequest, listConversations } from "../../api/forecast_chat"
import { ForecastVersion } from "../../dashboard/sales_forecasting/types"
import { toast } from "../ui/use-toast"
import { format } from "date-fns"
import { ChevronDown, ChevronUp, MessageSquare, Plus } from "lucide-react"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"

interface ConversationSelectorProps {
  forecastVersions: ForecastVersion[]
  currentForecastVersionId?: string
  onConversationChange: (conversation: Conversation | null) => void
  className?: string
}

export function ConversationSelector({
  forecastVersions,
  currentForecastVersionId,
  onConversationChange,
  className,
}: ConversationSelectorProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string>(currentForecastVersionId || forecastVersions[0]?.id || "")
  const [loading, setLoading] = useState(false)
  const [allConversations, setAllConversations] = useState<Conversation[]>([])
  const [selectedConversationId, setSelectedConversationId] = useState<string>("")
  const [selectedContextVersionIds, setSelectedContextVersionIds] = useState<string[]>([])
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isConversationsCollapsed, setIsConversationsCollapsed] = useState(true)
  
  const SHOW_BY_DEFAULT = 5
  const forecastConversations = allConversations.filter((conversation: Conversation) => conversation.forecast_version_id)
  const displayedConversations = isExpanded
    ? forecastConversations
    : forecastConversations.slice(0, SHOW_BY_DEFAULT)
  const hasMore = forecastConversations.length > SHOW_BY_DEFAULT

  // Load all conversations on mount
  useEffect(() => {
    async function loadAllConversations() {
      setLoadingConversations(true)
      try {
        const conversations = await listConversations()
        // Sort by updated_at descending (most recent first)
        const sorted = [...conversations].sort((a, b) => {
          const dateA = new Date(a.updated_at).getTime()
          const dateB = new Date(b.updated_at).getTime()
          return dateB - dateA
        })
        setAllConversations(sorted.filter((conversation: Conversation) => conversation.forecast_version_id))
      } catch (error) {
        console.error("Failed to load conversations:", error)
      } finally {
        setLoadingConversations(false)
      }
    }

    loadAllConversations()
  }, [])

  // Reload conversations when a new one is created
  async function reloadConversations() {
    try {
      const conversations = await listConversations()
      // Sort by updated_at descending (most recent first)
      const sorted = [...conversations].sort((a, b) => {
        const dateA = new Date(a.updated_at).getTime()
        const dateB = new Date(b.updated_at).getTime()
        return dateB - dateA
      })
      setAllConversations(sorted.filter((conversation: Conversation) => conversation.forecast_version_id))
    } catch (error) {
      console.error("Failed to reload conversations:", error)
    }
  }

  const handleStartConversation = useCallback(async (options?: { forceNew?: boolean }) => {
    if (!selectedVersionId) {
      onConversationChange(null)
      return
    }

    setLoading(true)
    try {
      const request: CreateConversationRequest = {
        forecast_version_id: selectedVersionId,
        context_forecast_version_ids: selectedContextVersionIds.filter((contextId: string) => contextId !== selectedVersionId),
        force_new: options?.forceNew,
      }
      
      const conversation = await createConversation(request)
      setSelectedConversationId(conversation.id)
      onConversationChange(conversation)
      setIsConversationsCollapsed(true)
      
      // Reload all conversations to include the new one
      await reloadConversations()
    } catch (error) {
      console.error("Failed to create/get conversation:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create conversation",
        variant: "destructive",
      })
      onConversationChange(null)
    } finally {
      setLoading(false)
    }
  }, [onConversationChange, selectedContextVersionIds, selectedVersionId])

  // Open ready to type: resume the current version's most recent conversation (or start its
  // first one) as soon as the panel mounts, instead of making the user click "Start chat".
  const autoStartedRef = useRef(false)
  useEffect(() => {
    if (autoStartedRef.current || selectedConversationId || !selectedVersionId) return
    autoStartedRef.current = true
    handleStartConversation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersionId])

  async function handleSelectExistingConversation(conversationId: string) {
    if (!conversationId) {
      onConversationChange(null)
      setSelectedConversationId("")
      setIsConversationsCollapsed(false)
      return
    }

    setLoading(true)
    try {
      const conversation = allConversations.find(c => c.id === conversationId)
      if (conversation) {
        setSelectedConversationId(conversationId)
        onConversationChange(conversation)
        // Collapse conversations when one is selected
        setIsConversationsCollapsed(true)
        // Update selected type/id based on conversation
        if (conversation.forecast_version_id) {
          setSelectedVersionId(conversation.forecast_version_id)
          setSelectedContextVersionIds(conversation.context_forecast_version_ids || [])
        }
      }
    } catch (error) {
      console.error("Failed to select conversation:", error)
      toast({
        title: "Error",
        description: "Failed to load conversation",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Auto-select current forecast or file if available
    // Only auto-select if we haven't already selected this ID
    if (currentForecastVersionId && selectedVersionId !== currentForecastVersionId) {
      setSelectedVersionId(currentForecastVersionId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentForecastVersionId])

  function handleVersionChange(id: string) {
    if (!id) {
      setSelectedVersionId("")
      setSelectedConversationId("")
      onConversationChange(null)
      return
    }
    
    setSelectedVersionId(id)
    setSelectedConversationId("") // Reset conversation selection when changing file/forecast
    onConversationChange(null)
  }

  function toggleContextVersion(versionId: string, checked: boolean) {
    setSelectedContextVersionIds(prev => {
      if (checked) {
        return prev.includes(versionId) ? prev : [...prev, versionId]
      }
      return prev.filter(id => id !== versionId)
    })
  }

  function getConversationLabel(conv: Conversation): string {
    if (conv.forecast_version_id) {
      const version = forecastVersions.find(v => v.id === conv.forecast_version_id)
      return version ? version.name : "Forecast"
    }
    return "Conversation"
  }

  function getConversationDate(conv: Conversation): string {
    const date = new Date(conv.updated_at)
    return format(date, "MMM d, h:mm a")
  }

  return (
    <div class={`flex flex-col gap-3 ${className || ""}`}>
      <div class="rounded-lg border border-gray-200 bg-white p-3">
        <div class="mb-2 flex items-start justify-between gap-3">
          <div>
            <div class="text-sm font-semibold text-gray-900">Forecast context</div>
            <div class="text-xs text-gray-500">Choose the forecast this chat should analyze or edit.</div>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={loading || !selectedVersionId}
            onClick={() => handleStartConversation({ forceNew: true })}
            className="h-8 gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            New chat
          </Button>
        </div>

        <Select
          value={selectedVersionId}
          onValueChange={handleVersionChange}
          disabled={loading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select forecast" />
          </SelectTrigger>
          <SelectContent>
            {forecastVersions.map((version) => (
              <SelectItem key={version.id} value={version.id}>
                {version.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {forecastVersions.length > 1 && (
          <div class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-2">
            <div class="mb-1 text-xs font-medium text-gray-600">Optional context versions</div>
            <div class="flex max-h-28 flex-col gap-1 overflow-auto">
              {forecastVersions
                .filter((version) => version.id !== selectedVersionId)
                .map((version) => (
                  <label key={version.id} class="flex items-center gap-2 text-xs text-gray-700">
                    <Checkbox
                      checked={selectedContextVersionIds.includes(version.id)}
                      onCheckedChange={(checked: boolean | "indeterminate") => toggleContextVersion(version.id, checked === true)}
                    />
                    <span class="truncate">{version.name}</span>
                  </label>
                ))}
            </div>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || !selectedVersionId}
          onClick={() => handleStartConversation()}
          className="mt-3 w-full"
        >
          Use this context
        </Button>
      </div>

      {/* Conversations List */}
      <div class="flex flex-col">
        <button
          onClick={() => setIsConversationsCollapsed(!isConversationsCollapsed)}
          class="flex items-center justify-between text-sm font-medium text-gray-700 mb-2 hover:text-gray-900 transition-colors w-full"
        >
          <span>Conversations</span>
          {isConversationsCollapsed ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          )}
        </button>
        {!isConversationsCollapsed && (
          <>
            {loadingConversations ? (
              <div class="text-sm text-gray-500 py-4">Loading conversations...</div>
            ) : forecastConversations.length === 0 ? (
              <div class="text-sm text-gray-500 py-4">No conversations yet. Start a new chat above.</div>
            ) : (
              <div class="flex flex-col gap-1">
                {displayedConversations.map((conv) => {
                  const isSelected = selectedConversationId === conv.id
                  return (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectExistingConversation(conv.id)}
                      disabled={loading}
                      class={`
                        flex items-start gap-2 p-2 rounded-md text-left transition-colors
                        ${isSelected 
                          ? "bg-blue-50 border border-blue-200" 
                          : "hover:bg-gray-50 border border-transparent"
                        }
                        ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                      `}
                    >
                      <MessageSquare className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isSelected ? "text-blue-600" : "text-gray-400"}`} />
                      <div class="flex-1 min-w-0">
                        <div class={`text-sm font-medium ${isSelected ? "text-blue-900" : "text-gray-900"}`}>
                          {getConversationLabel(conv)}
                        </div>
                        <div class={`text-xs ${isSelected ? "text-blue-600" : "text-gray-500"}`}>
                          {getConversationDate(conv)}
                        </div>
                      </div>
                    </button>
                  )
                })}
                {hasMore && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    class="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" />
                        Show {forecastConversations.length - SHOW_BY_DEFAULT} more
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
